import * as React from "react";
import { Suspense } from "react";

import { BellOutlined } from "@ant-design/icons";
import { formatUserName } from "@impero-com/common/utils/formatting";
import { t } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { Button, Svg, Tag, Tooltip } from "@impero-com/ui";
import RemoveIconSvg from "@impero-com/ui/img/remove.svg";
import Skeleton from "react-loading-skeleton";

import {
    UserGroupOverviewResource,
    UserOverview,
    EntityView,
} from "api/generated/model";
import { TagList } from "components/tag/tagList";
import GroupIconSvg from "img/group-icon.svg";
import { useTagsSuspenseQuery } from "org/tags/queries/useTagsQuery";
import { Guid } from "utils/guid";
import { mapAssignmentRowUserGroupToUserGroupOverviewResource } from "utils/userGroup";

import * as theme from "./theme.css";
import { AssignmentRow, ReviewerType } from "./types";
import { getRecipientsTooltipText } from "./utils";

class UserCell extends React.PureComponent<{ userOverview: UserOverview }> {
    render = () => {
        const { userOverview } = this.props;
        return (
            <>
                <div className={theme.user}>{formatUserName(userOverview)}</div>
                <div className={theme.email}>{userOverview.email}</div>
            </>
        );
    };
}

class UserGroupCell extends React.PureComponent<{
    userGroupOverviewResource: UserGroupOverviewResource;
}> {
    render = () => {
        const { userGroupOverviewResource } = this.props;
        return (
            <div className={theme.userGroupWithIcon}>
                <div>
                    <GroupInfo
                        {...this.props}
                        userCount={userGroupOverviewResource.userCount}
                    />
                    <div className={theme.email}>
                        {t("Control_Assignee_Group_Assigned_Individually")}
                    </div>
                </div>
                <GroupIcon />
            </div>
        );
    };
}

class UserGroupPoolResponsibleCell extends React.PureComponent<{
    userGroupOverviewResource: UserGroupOverviewResource;
    defaultClaimant: UserOverview | null;
}> {
    render = () => {
        const { userGroupOverviewResource, defaultClaimant } = this.props;
        return (
            <div className={theme.userGroupWithIcon}>
                <div>
                    <GroupInfo
                        userGroupOverviewResource={userGroupOverviewResource}
                        userCount={userGroupOverviewResource.distinctUserCount}
                    />
                    <div className={theme.email}>
                        {t("Control_Assignee_UserGroupPool_Label")}
                        {defaultClaimant
                            ? ` ${t(
                                  "Control_Assignee_UserGroupPool_Claimant_Label",
                                  { user: formatUserName(defaultClaimant) }
                              )}`
                            : null}
                    </div>
                </div>
                <GroupIcon />
            </div>
        );
    };
}

class GroupInfo extends React.PureComponent<{
    userGroupOverviewResource: UserGroupOverviewResource;
    userCount: number;
}> {
    render = () => {
        const { userGroupOverviewResource, userCount } = this.props;
        return (
            <div className={theme.user}>
                <span>{userGroupOverviewResource.userGroup.name} </span>
                <span className={theme.userCount}>({userCount})</span>
            </div>
        );
    };
}

class GroupIcon extends React.PureComponent {
    render = () => {
        return (
            <div className={theme.reviewerGroupIcon}>
                <span>
                    <Svg src={GroupIconSvg} className={theme.groupIcon} />
                </span>
            </div>
        );
    };
}

class ResponsibleCell extends React.PureComponent<{
    assignmentRow: AssignmentRow;
}> {
    render = () => {
        const { assignmentRow } = this.props;
        switch (assignmentRow.type) {
            case "User":
                return <UserCell userOverview={assignmentRow.userOverview} />;
            case "Group":
                return (
                    <UserGroupCell
                        userGroupOverviewResource={mapAssignmentRowUserGroupToUserGroupOverviewResource(
                            assignmentRow.userGroup
                        )}
                    />
                );
            case "Pool":
                return (
                    <UserGroupPoolResponsibleCell
                        userGroupOverviewResource={
                            assignmentRow.userGroupOverviewResource
                        }
                        defaultClaimant={assignmentRow.defaultClaimantUser}
                    />
                );
            default:
                return assertNever(assignmentRow);
        }
    };
}

class ReviewerCell extends React.PureComponent<{
    reviewer: ReviewerType | null;
}> {
    render = () => {
        const { reviewer } = this.props;
        if (reviewer) {
            switch (reviewer.type) {
                case "User":
                    return <UserCell userOverview={reviewer.userOverview} />;
                case "Pool":
                    return (
                        <div className={theme.userGroupWithIcon}>
                            <GroupInfo
                                userGroupOverviewResource={
                                    reviewer.userGroupOverviewResource
                                }
                                userCount={
                                    reviewer.userGroupOverviewResource
                                        .distinctUserCount
                                }
                            />
                            <GroupIcon />
                        </div>
                    );
                default:
                    assertNever(reviewer);
            }
        } else {
            return (
                <span className={theme.notAssignedReviewerOrTags}>
                    {t("Control_Reviewer_NotAssigned")}
                </span>
            );
        }
    };
}

class FirstReviewerCell extends React.PureComponent<{
    assignmentRow: AssignmentRow;
}> {
    render = () => {
        const { assignmentRow } = this.props;
        const reviewer =
            assignmentRow.firstReviewer || assignmentRow.finalReviewer;
        return <ReviewerCell reviewer={reviewer} />;
    };
}

class SecondReviewerCell extends React.PureComponent<{
    assignmentRow: AssignmentRow;
}> {
    render = () => {
        const { assignmentRow } = this.props;
        // we render the second reviewer only when there is a first reviewer, otherwise the finalReviewer is shown in the previous `Reviewer` column
        const hasTwoReviewers = !!assignmentRow.firstReviewer;
        const reviewer = hasTwoReviewers ? assignmentRow.finalReviewer : null;
        return <ReviewerCell reviewer={reviewer} />;
    };
}

interface EntityCellProps {
    assignmentRow: AssignmentRow;
    entitiesById: Map<number, EntityView>;
}

function EntityCell({ assignmentRow, entitiesById }: EntityCellProps) {
    const { entityId } = assignmentRow;
    if (entityId === undefined) {
        return null;
    }
    const assignedEntity = entitiesById.get(entityId);
    if (!assignedEntity) {
        return null;
    }
    return <Tag variant="entity">{assignedEntity.name}</Tag>;
}

function TagsCell({ tagIds }: { tagIds: Guid[] }) {
    if (tagIds.length === 0) {
        return (
            <span className={theme.notAssignedReviewerOrTags}>
                {t("Control_Tags_NotAssigned")}
            </span>
        );
    }
    return (
        <Suspense fallback={<Skeleton />}>
            <AssignedTags tagIds={tagIds} />
        </Suspense>
    );
}

function AssignedTags({ tagIds }: { tagIds: Guid[] }) {
    const { data } = useTagsSuspenseQuery();

    return (
        <TagList
            mode="LIST"
            tagCategories={data.tagCategories}
            tags={data.tags.filter((tag) => tagIds.includes(tag.id))}
        />
    );
}

class NotifyCell extends React.PureComponent<{
    assignmentRow: AssignmentRow;
    numberOfUserRecipients: number;
    numberOfGroupRecipients: number;
    disabled: boolean;
}> {
    render = () => {
        const {
            assignmentRow,
            numberOfUserRecipients,
            numberOfGroupRecipients,
            disabled,
        } = this.props;

        const text = getRecipientsTooltipText({
            t,
            assignmentRow,
            numberOfUserRecipients,
            numberOfGroupRecipients,
        });
        return (
            <Tooltip title={text} disabled={disabled}>
                <span
                    className={`${theme.notifyCell} ${
                        disabled ? theme.disabledNotifyCell : ""
                    }`}
                >
                    <BellOutlined
                        className={
                            assignmentRow.notify ? theme.active : undefined
                        }
                    />
                </span>
            </Tooltip>
        );
    };
}

class DeleteCell extends React.PureComponent<{
    assignmentId: Guid;
    onClick: (assignmentId: Guid) => void;
    disabled: boolean;
}> {
    render = () => {
        const { assignmentId, disabled } = this.props;
        return (
            <Button
                id={`delete-assignment-button-${assignmentId}`}
                variant="link"
                className={theme.removeButton}
                onClick={this.handleClick}
                disabled={disabled}
            >
                <Svg className={theme.removeIcon} src={RemoveIconSvg} />
            </Button>
        );
    };

    private handleClick = (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        const { assignmentId, onClick } = this.props;
        onClick(assignmentId);
    };
}

export {
    ResponsibleCell,
    FirstReviewerCell,
    SecondReviewerCell,
    EntityCell,
    TagsCell,
    NotifyCell,
    DeleteCell,
    GroupIcon,
};
